import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useOnboardingController, useTourController } from '../controller';
import { useOnboardingStateQuery } from '../hooks/useOnboardingStateQuery';
import { OnboardingOverlay } from './OnboardingOverlay';
import { TourOverlay } from './TourOverlay';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { usePlayerAccess } from '@/features/team/controller/usePlayerAccess';
import { useIsPhone } from '@/hooks/useIsPhone';

interface OnboardingProviderProps {
  children: ReactNode;
}

/**
 * `GET /onboarding` part d'ici, et de nulle part ailleurs.
 *
 * L'ancienne version déclenchait la requête depuis un `useEffect` gardé par
 * `!state` : le double montage StrictMode et tout changement d'identité de
 * `user` (rafraîchissement de jeton) en produisaient 2 à 3 par chargement de
 * page. React Query dédoublonne sur une clé stable et respecte un `staleTime`.
 */
export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const user = useAuthController((s) => s.user);
  const hydrate = useOnboardingController((s) => s.hydrate);
  const clearPause = useOnboardingController((s) => s.clearPause);
  const setTourUser = useTourController((s) => s.setUser);
  const userId = user?.id ?? null;

  // Logging out does not reload the page: reset what belongs to the previous account.
  useEffect(() => {
    setTourUser(userId);
    clearPause();
  }, [userId, setTourUser, clearPause]);

  const { data } = useOnboardingStateQuery(Boolean(user));
  const access = usePlayerAccess();
  const isPhone = useIsPhone();

  useEffect(() => {
    // Waiting for the player's teams avoids starting on a Stats step that would then vanish.
    if (data && !access.isLoading) hydrate({ ...data, canOpenStats: access.stats, isPhone });
  }, [data, hydrate, access.isLoading, access.stats, isPhone]);

  return (
    <>
      {children}
      <OnboardingOverlay />
      <TourOverlay />
    </>
  );
};
